import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { Gavel, ArrowRight, CheckCircle2, Upload, Clock, FileText } from "lucide-react";

function HacizeGidildiBtn({ g, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/haciz-gundemi/${g.id}/hacize-gidildi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hacize gidildi kaydedildi, tutanak yüklendi");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Yüklenemedi"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  if (g.status === "hacize_gidildi") {
    return (
      <span data-testid={`avukat-gidildi-${g.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#2F6E4E]/10 text-[#2F6E4E]">
        <CheckCircle2 className="w-3.5 h-3.5" /> Hacize Gidildi{g.hacize_gidildi_at ? ` · ${fmtDate(g.hacize_gidildi_at)}` : ""}
      </span>
    );
  }
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} data-testid={`avukat-tutanak-input-${g.id}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`avukat-hacize-gidildi-${g.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#B8933F] hover:bg-[#E5850C] text-white rounded-sm text-xs font-semibold transition-colors duration-150 disabled:opacity-60">
        <Upload className="w-3.5 h-3.5" /> {busy ? "Yükleniyor…" : "Hacize Gidildi (Tutanak)"}
      </button>
    </>
  );
}

export default function AvukatGorevlerim() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/haciz-gundemi/hazir")
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Görevler yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addNot = async (rid, musteri) => {
    if (!rid) { toast.error("Dosya bulunamadı"); return; }
    const text = window.prompt(`${musteri} — Haciz/Görev notu:`);
    if (!text || !text.trim()) return;
    try { await api.post(`/records/${rid}/haciz-not`, { text: text.trim() }); toast.success("Not eklendi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Not eklenemedi"); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="kicker mb-1">AVUKAT — GÖREVLERİM</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Hacize Hazır Dosyalar</h1>
        <p className="text-sm text-[#8C8377] mt-1">Haciz gündeminde "Tamamlandı" işaretlenen dosyalar. Hacize çıkınca <b>haciz tutanağı yüklemek zorunludur</b>.</p>
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden" data-testid="avukat-gorevlerim">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
              <th className="px-3 py-2.5 kicker text-left">Müşteri</th>
              <th className="px-3 py-2.5 kicker text-left">Dosya No</th>
              <th className="px-3 py-2.5 kicker text-left">İcra Dairesi</th>
              <th className="px-3 py-2.5 kicker text-left">Tebliğ Şekli</th>
              <th className="px-3 py-2.5 kicker text-left">Bekleme</th>
              <th className="px-3 py-2.5 kicker text-right">Kalan</th>
              <th className="px-3 py-2.5 kicker text-right">İşlem</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-[#8C8377]" data-testid="avukat-gorevlerim-empty">Hacize hazır dosya yok.</td></tr>
              ) : rows.map((g) => (
                <tr key={g.id} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`avukat-gorev-${g.id}`}>
                  <td className="px-3 py-2 font-medium flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#2F6E4E]" /> {g.record?.musteri || "-"}</td>
                  <td className="px-3 py-2 tabular-nums">{g.record?.dosya_no || "-"}</td>
                  <td className="px-3 py-2 text-[#8C8377]">{g.record?.icra_dairesi || "-"}</td>
                  <td className="px-3 py-2">{g.teblig_sekli || "-"}</td>
                  <td className="px-3 py-2">
                    {g.done_gun != null ? (
                      <span data-testid={`avukat-bekleme-${g.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold ${g.done_gun >= 7 ? "bg-[#A63A3A]/10 text-[#A63A3A]" : "bg-[#B8933F]/10 text-[#B8933F]"}`}>
                        <Clock className="w-3 h-3" /> {g.done_gun} gün
                      </span>
                    ) : "-"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(g.record?.kalan || 0)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <HacizeGidildiBtn g={g} onDone={load} />
                      <button onClick={() => addNot(g.record_id, g.record?.musteri || "Dosya")} data-testid={`avukat-not-${g.id}`} title="Not Ekle" className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E7DECC] hover:bg-[#EFE7D6] text-[#26231E] rounded-sm text-xs font-medium transition-colors duration-150">
                        <FileText className="w-3.5 h-3.5 text-[#B8933F]" /> Not Ekle
                      </button>
                      <button onClick={() => navigate(`/icra/${g.record_id}`)} data-testid={`avukat-gorev-ac-${g.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-xs font-medium transition-colors duration-150">
                        <Gavel className="w-3.5 h-3.5" /> Dosyayı Aç <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
